/**
 * Gameplan → HTML cheatsheet export.
 *
 * Produces a self-contained, downloadable HTML file: gameplan title,
 * description, then per-line { inline SVG diagram + ordered
 * walk-through of every node }. The walk-through includes entity
 * descriptions, steps, edge labels, user notes, and links to media
 * refs when available.
 *
 * No React or DOM dependencies — pure string templating. Drop the
 * result into a Blob and href-trigger a download.
 */
import type {
  Counter,
  MediaRefsIndex,
  Move,
  Position,
} from '../types/domain';
import type {
  Gameplan,
  Line,
  LineEdge,
  LineNode,
  LineNodeRef,
} from '../types/gameplan';
import {
  LOOPBACK_GUTTER,
  MARGIN_X,
  MARGIN_Y,
  NODE_H,
  NODE_W,
  layoutLine,
} from './gameplan-layout';
import type { LayoutEdge, LayoutNode } from './gameplan-layout';

// Must match LinesCanvas.tsx — arrowhead length in SVG units. The
// path stops this far before the target card so the marker's base
// meets the curve and the tip lands at the card edge.
const ARROW_LEN = 8;

// -----------------------------------------------------------------
// Public API
// -----------------------------------------------------------------

/**
 * Builds the full HTML document for a gameplan cheatsheet.
 */
export function exportGameplanHtml(
  gameplan: Gameplan,
  positions: Position[],
  moves: Move[],
  counters: Counter[],
  mediaRefs: MediaRefsIndex | null,
): string {
  const posById = new Map(positions.map((p) => [p.id, p]));
  const moveById = new Map(moves.map((m) => [m.id, m]));
  const counterById = new Map(counters.map((c) => [c.id, c]));
  const lookups = { posById, moveById, counterById };

  const linesHtml = gameplan.lines
    .map((line, idx) => renderLine(line, idx, lookups, mediaRefs))
    .join('\n');

  const title = escapeHtml(gameplan.name);
  const desc = gameplan.description ? escapeHtml(gameplan.description) : '';
  const exportedAt = new Date().toLocaleString();

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${title} — bjjmap cheatsheet</title>
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<style>
  :root {
    --bg: #0a0b10;
    --surface: #11141d;
    --surface-2: #181c27;
    --text: #e6e8ef;
    --muted: #98a0b0;
    --cyan: #9be7ff;
    --amber: #ffcc88;
    --rose: #ffaab2;
    --border: rgba(255,255,255,0.08);
  }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    background: var(--bg);
    color: var(--text);
    font: 14px/1.55 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
  }
  .page { max-width: 880px; margin: 0 auto; padding: 32px 24px 64px; }
  h1 { font-size: 28px; margin: 0 0 6px; letter-spacing: -0.01em; }
  .meta { font-size: 11px; color: var(--muted); margin-bottom: 24px; letter-spacing: 0.4px; text-transform: uppercase; }
  .description { color: var(--muted); margin: 0 0 32px; line-height: 1.6; }
  .line { margin: 48px 0; border-top: 1px solid var(--border); padding-top: 28px; }
  .line-header { display: flex; align-items: baseline; gap: 10px; margin-bottom: 8px; }
  .line-name { font-size: 20px; font-weight: 600; margin: 0; }
  .line-counts { font-size: 11px; color: var(--muted); }
  .line-notes { color: var(--muted); font-size: 13px; margin: 4px 0 14px; }
  .diagram {
    background: rgba(0,0,0,0.30);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 14px;
    overflow-x: auto;
    margin: 18px 0 24px;
  }
  .diagram svg { display: block; max-width: 100%; height: auto; }
  .walkthrough { display: flex; flex-direction: column; gap: 14px; }
  .step {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 14px 16px;
  }
  .step-head { display: flex; align-items: baseline; gap: 10px; margin-bottom: 6px; flex-wrap: wrap; }
  .step-index { color: var(--muted); font-size: 11px; font-variant-numeric: tabular-nums; }
  .step-tag {
    font-size: 9px; letter-spacing: 0.6px; text-transform: uppercase;
    padding: 1px 6px; border-radius: 3px;
  }
  .step-tag.position { background: rgba(155,231,255,0.10); color: var(--cyan); border: 1px solid rgba(155,231,255,0.30); }
  .step-tag.move     { background: rgba(255,200,120,0.10); color: var(--amber); border: 1px solid rgba(255,200,120,0.30); }
  .step-tag.counter  { background: rgba(255,128,140,0.12); color: var(--rose); border: 1px solid rgba(255,128,140,0.30); }
  .step-name { font-size: 15px; font-weight: 600; }
  .step-meta { color: var(--muted); font-size: 12px; margin: 4px 0 0; }
  .step-desc { color: var(--text); font-size: 13px; margin: 8px 0; line-height: 1.55; }
  .step-steps { margin: 8px 0 0; padding-left: 22px; }
  .step-steps li { margin-bottom: 6px; font-size: 13px; line-height: 1.5; }
  .step-steps li .stitle { color: var(--cyan); font-weight: 500; }
  .step-notes {
    margin-top: 10px; padding: 8px 10px;
    background: rgba(155,231,255,0.06);
    border-left: 2px solid var(--cyan);
    border-radius: 0 4px 4px 0;
    font-size: 13px; color: var(--cyan); white-space: pre-wrap;
  }
  .edge-cue {
    font-size: 12px; color: var(--muted); margin: 6px 0 0;
    padding: 4px 10px; background: rgba(255,255,255,0.04);
    border-left: 2px solid rgba(155,231,255,0.40);
    border-radius: 0 4px 4px 0;
  }
  .media { margin-top: 10px; display: flex; flex-wrap: wrap; gap: 8px; }
  .media a {
    font-size: 11px; padding: 3px 8px; border-radius: 4px;
    background: rgba(155,231,255,0.10); color: var(--cyan);
    text-decoration: none; border: 1px solid rgba(155,231,255,0.20);
  }
  .empty { color: var(--muted); font-style: italic; }
  @media print {
    body { background: white; color: black; }
    :root {
      --bg: white; --surface: #f5f7fa; --surface-2: #eef1f5;
      --text: #111; --muted: #555;
      --border: #ddd; --cyan: #146db3; --amber: #b56e00; --rose: #b3204a;
    }
    .line { page-break-inside: avoid; }
    .step { page-break-inside: avoid; }
  }
</style>
</head>
<body>
<div class="page">
  <div style="display:flex;align-items:center;gap:8px;margin-bottom:22px">
    <svg width="22" height="22" viewBox="0 0 64 64" fill="none" role="img" aria-label="bjjmap">
      <ellipse cx="32" cy="32" rx="28" ry="14" transform="rotate(-32 32 32)" stroke="#7aaec0" stroke-width="2" opacity="0.55"></ellipse>
      <circle cx="26" cy="27" r="12" fill="#d4a070"></circle>
      <circle cx="39" cy="39" r="12" fill="#7aaec0"></circle>
    </svg>
    <span style="font-weight:700;font-size:16px;letter-spacing:-0.02em">bjjmap</span>
  </div>
  <h1>${title}</h1>
  <div class="meta">gameplan · ${gameplan.lines.length} line${gameplan.lines.length === 1 ? '' : 's'} · exported ${escapeHtml(exportedAt)}</div>
  ${desc ? `<p class="description">${desc}</p>` : ''}
  ${gameplan.lines.length === 0 ? '<p class="empty">No lines yet.</p>' : linesHtml}
</div>
</body>
</html>`;
}

/**
 * Triggers a browser download of the gameplan cheatsheet.
 */
export function downloadGameplanCheatsheet(
  gameplan: Gameplan,
  positions: Position[],
  moves: Move[],
  counters: Counter[],
  mediaRefs: MediaRefsIndex | null,
): void {
  const html = exportGameplanHtml(gameplan, positions, moves, counters, mediaRefs);
  triggerDownload(html, 'text/html;charset=utf-8', `${safeFilename(gameplan.name)}.html`);
}

/**
 * Rasterizes a single line's SVG into a PNG file and triggers a
 * download. The PNG is 2× the SVG dimensions (retina-ish), painted
 * on a dark surface that matches the editor's background.
 *
 * Returns a promise that resolves after the download is triggered;
 * rejects if the SVG fails to load as an image (rare, e.g. malformed
 * markup).
 */
export async function downloadLinePng(
  line: Line,
  gameplanName: string,
  positions: Position[],
  moves: Move[],
  counters: Counter[],
): Promise<void> {
  if (line.nodes.length === 0) return;
  const lookups = {
    posById: new Map(positions.map((p) => [p.id, p])),
    moveById: new Map(moves.map((m) => [m.id, m])),
    counterById: new Map(counters.map((c) => [c.id, c])),
  };

  const layout = layoutLine(line);
  const svgString = serializeLineSvg(line, lookups);

  // Wrap in a full SVG with explicit XML decl + namespace so it loads
  // reliably as an Image source across browsers.
  const fullSvg = `<?xml version="1.0" encoding="UTF-8"?>\n${svgString}`;

  // Encode as data URI rather than blob: data: URIs play nicer with
  // <img> + canvas drawImage across Safari versions.
  const dataUri =
    'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(fullSvg);

  const img = await loadImage(dataUri);
  const scale = 2;
  const canvas = document.createElement('canvas');
  canvas.width = layout.width * scale;
  canvas.height = layout.height * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to acquire 2D context for PNG export.');
  // Solid background — the editor canvas paints over a dark surface
  // and the line strokes are tuned for it; a transparent PNG would
  // be hard to read on a light backdrop.
  ctx.fillStyle = '#0a0b10';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.scale(scale, scale);
  ctx.drawImage(img, 0, 0, layout.width, layout.height);

  const pngBlob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => {
      if (b) resolve(b);
      else reject(new Error('canvas.toBlob returned null'));
    }, 'image/png');
  });

  const filename = `${safeFilename(gameplanName)} — ${safeFilename(line.name)}.png`;
  triggerBlobDownload(pngBlob, filename);
}

// -----------------------------------------------------------------
// Download helpers
// -----------------------------------------------------------------

function triggerDownload(
  data: string,
  mime: string,
  filename: string,
): void {
  triggerBlobDownload(new Blob([data], { type: mime }), filename);
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function safeFilename(s: string): string {
  return s.replace(/[^a-z0-9-_ ]/gi, '').trim() || 'gameplan';
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('SVG image failed to load.'));
    img.src = src;
  });
}

// -----------------------------------------------------------------
// Per-line rendering
// -----------------------------------------------------------------

type Lookups = {
  posById: Map<string, Position>;
  moveById: Map<string, Move>;
  counterById: Map<string, Counter>;
};

function renderLine(
  line: Line,
  idx: number,
  lookups: Lookups,
  mediaRefs: MediaRefsIndex | null,
): string {
  const lineName = escapeHtml(line.name || `Line ${idx + 1}`);
  const counts = `${line.nodes.length} node${line.nodes.length === 1 ? '' : 's'} · ${line.edges.length} edge${line.edges.length === 1 ? '' : 's'}`;
  const notes = line.notes ? `<div class="line-notes">${escapeHtml(line.notes)}</div>` : '';

  if (line.nodes.length === 0) {
    return `<section class="line">
      <div class="line-header"><h2 class="line-name">${lineName}</h2><span class="line-counts">${counts}</span></div>
      ${notes}
      <p class="empty">No nodes in this line yet.</p>
    </section>`;
  }

  const svg = serializeLineSvg(line, lookups);
  const walkthrough = renderWalkthrough(line, lookups, mediaRefs);

  return `<section class="line">
  <div class="line-header"><h2 class="line-name">${lineName}</h2><span class="line-counts">${counts}</span></div>
  ${notes}
  <div class="diagram">${svg}</div>
  <div class="walkthrough">${walkthrough}</div>
</section>`;
}

// -----------------------------------------------------------------
// SVG serialization — static, no React, no event handlers.
// -----------------------------------------------------------------

function serializeLineSvg(line: Line, lookups: Lookups): string {
  const layout = layoutLine(line);
  if (layout.nodes.length === 0) return '';

  const defs = `<defs>
    <marker id="bjjx-arrow-fwd" viewBox="0 0 10 10" refX="0" refY="5" markerUnits="userSpaceOnUse" markerWidth="${ARROW_LEN}" markerHeight="${ARROW_LEN}" orient="auto-start-reverse">
      <path d="M 0 0 L 10 5 L 0 10 z" fill="#8a92a0" />
    </marker>
    <marker id="bjjx-arrow-back" viewBox="0 0 10 10" refX="0" refY="5" markerUnits="userSpaceOnUse" markerWidth="${ARROW_LEN}" markerHeight="${ARROW_LEN}" orient="auto-start-reverse">
      <path d="M 0 0 L 10 5 L 0 10 z" fill="#b395d2" />
    </marker>
  </defs>`;

  const edgesSvg = layout.edges.map((le) => renderEdge(le)).join('\n');
  const labelsSvg = layout.edges
    .filter((le) => !!le.edge.label)
    .map((le) => renderEdgeLabel(le))
    .join('\n');
  const nodesSvg = layout.nodes
    .map((ln) => renderNode(ln, lookups))
    .join('\n');

  // Account for MARGIN_X / MARGIN_Y / LOOPBACK_GUTTER constants used by
  // the layout so we can declare them here too — only for layout
  // consumers that need them. They're already baked into width/height
  // so we just emit the svg.
  void MARGIN_X;
  void MARGIN_Y;
  void LOOPBACK_GUTTER;

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${layout.width} ${layout.height}" width="${layout.width}" height="${layout.height}">
    ${defs}
    ${edgesSvg}
    ${labelsSvg}
    ${nodesSvg}
  </svg>`;
}

function renderEdge(le: LayoutEdge): string {
  const { from, to, kind } = le;
  let d: string;
  if (kind === 'forward') {
    const x1 = from.x;
    const y1 = from.y + NODE_H / 2;
    const x2 = to.x;
    const y2 = to.y - NODE_H / 2 - ARROW_LEN;
    const midY = (y1 + y2) / 2;
    d = `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`;
    return `<path d="${d}" fill="none" stroke="#8a92a0" stroke-width="1.4" opacity="0.85" marker-end="url(#bjjx-arrow-fwd)" />`;
  }
  const x1 = from.x + NODE_W / 2;
  const y1 = from.y;
  const x2 = to.x + NODE_W / 2 + ARROW_LEN;
  const y2 = to.y;
  const bulge = 40 + Math.abs(from.layer - to.layer) * 8;
  const cx1 = x1 + bulge;
  const cy1 = y1;
  const cx2 = x2 + bulge;
  const cy2 = y2;
  d = `M ${x1} ${y1} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${x2} ${y2}`;
  return `<path d="${d}" fill="none" stroke="#b395d2" stroke-width="1.4" stroke-dasharray="4 3" opacity="0.85" marker-end="url(#bjjx-arrow-back)" />`;
}

function renderEdgeLabel(le: LayoutEdge): string {
  if (!le.edge.label) return '';
  const mid = edgeMidpoint(le);
  const text = escapeHtml(truncate(le.edge.label, 32));
  const w = Math.min(Math.max(text.length * 5.4 + 12, 36), 200);
  const x = mid.x;
  const y = mid.y - 14;
  return `<g>
    <rect x="${x - w / 2}" y="${y - 9}" width="${w}" height="16" rx="6" ry="6" fill="rgba(20,22,30,0.92)" stroke="rgba(155,231,255,0.30)" stroke-width="0.8" />
    <text x="${x}" y="${y + 2.5}" text-anchor="middle" font-size="9" fill="#cdf6ff">${text}</text>
  </g>`;
}

function edgeMidpoint(le: LayoutEdge): { x: number; y: number } {
  const { from, to, kind } = le;
  if (kind === 'forward') {
    const y1 = from.y + NODE_H / 2;
    const y2 = to.y - NODE_H / 2 - ARROW_LEN;
    return {
      x: (from.x + to.x) / 2,
      y: (y1 + y2) / 2,
    };
  }
  const bulge = 40 + Math.abs(from.layer - to.layer) * 8;
  const x1 = from.x + NODE_W / 2;
  const x2 = to.x + NODE_W / 2 + ARROW_LEN;
  return {
    x: (x1 + x2) / 2 + 0.75 * bulge,
    y: (from.y + to.y) / 2,
  };
}

function renderNode(ln: LayoutNode, lookups: Lookups): string {
  const kind = ln.node.ref.kind;
  // Mirror LinesCanvas: terminal positions (zone 'submitted' /
  // 'scrambled') get the violet palette to read distinctly.
  let terminal = false;
  if (ln.node.ref.kind === 'position') {
    const p = lookups.posById.get(ln.node.ref.id);
    terminal = p?.zone === 'submitted' || p?.zone === 'scrambled';
  }
  const palette = kindPalette(kind, terminal);
  const x = ln.x - NODE_W / 2;
  const y = ln.y - NODE_H / 2;
  const label = escapeHtml(
    truncate(entityDisplayName(ln.node.ref, lookups), 20),
  );
  return `<g>
    <rect x="${x}" y="${y}" width="${NODE_W}" height="${NODE_H}" rx="8" ry="8" fill="${palette.fill}" stroke="${palette.stroke}" stroke-width="1.2" />
    <text x="${x + 8}" y="${y + 12}" font-size="8" fill="${palette.tag}" style="text-transform:uppercase;letter-spacing:0.6px">${kind}</text>
    <text x="${ln.x}" y="${ln.y + 8}" text-anchor="middle" font-size="11" font-weight="600" fill="${palette.text}">${label}</text>
  </g>`;
}

function kindPalette(
  kind: 'position' | 'move' | 'counter',
  terminal = false,
) {
  switch (kind) {
    case 'position':
      if (terminal) {
        return {
          fill: 'rgba(164,142,176,0.14)',
          stroke: 'rgba(164,142,176,0.50)',
          text: '#e2cfee',
          tag: 'rgba(220,180,235,0.75)',
        };
      }
      return {
        fill: 'rgba(155,231,255,0.10)',
        stroke: 'rgba(155,231,255,0.35)',
        text: '#cdf6ff',
        tag: 'rgba(155,231,255,0.55)',
      };
    case 'move':
      return {
        fill: 'rgba(255,200,120,0.10)',
        stroke: 'rgba(255,200,120,0.35)',
        text: '#ffd9a8',
        tag: 'rgba(255,200,120,0.6)',
      };
    case 'counter':
      return {
        fill: 'rgba(255,128,140,0.12)',
        stroke: 'rgba(255,128,140,0.40)',
        text: '#ffc0c8',
        tag: 'rgba(255,128,140,0.65)',
      };
  }
}

// -----------------------------------------------------------------
// Walk-through (ordered prose)
// -----------------------------------------------------------------

function renderWalkthrough(
  line: Line,
  lookups: Lookups,
  mediaRefs: MediaRefsIndex | null,
): string {
  const order = walkthroughOrder(line);
  // Map nodeId → list of outgoing edges (for inline edge-cue hints).
  const outgoingByNode = new Map<string, LineEdge[]>();
  for (const e of line.edges) {
    const list = outgoingByNode.get(e.from) ?? [];
    list.push(e);
    outgoingByNode.set(e.from, list);
  }

  const items: string[] = [];
  order.forEach((node, i) => {
    items.push(renderStep(node, i + 1, lookups, mediaRefs));
    // Inline edge labels coming OUT of this node — surfaces "what
    // condition triggers each branch" right in the prose flow.
    const outs = outgoingByNode.get(node.id) ?? [];
    for (const e of outs) {
      if (!e.label) continue;
      const target = line.nodes.find((n) => n.id === e.to);
      const targetName = target
        ? entityDisplayName(target.ref, lookups)
        : '(removed)';
      items.push(
        `<div class="edge-cue"><strong>→ ${escapeHtml(targetName)}:</strong> ${escapeHtml(e.label)}</div>`,
      );
    }
  });
  return items.join('\n');
}

function walkthroughOrder(line: Line): LineNode[] {
  // BFS from forward roots (nodes with no forward in-edges), then
  // append anything left over (disconnected / cycle-only nodes).
  // Detect back-edges with a DFS, similar to gameplan-layout.
  const outByFrom = new Map<string, LineEdge[]>();
  for (const e of line.edges) {
    const list = outByFrom.get(e.from) ?? [];
    list.push(e);
    outByFrom.set(e.from, list);
  }
  const stateMap = new Map<string, 'unvisited' | 'inStack' | 'done'>();
  for (const n of line.nodes) stateMap.set(n.id, 'unvisited');
  const backEdgeIds = new Set<string>();
  const dfs = (start: string) => {
    type Frame = { id: string; outIdx: number };
    const stack: Frame[] = [{ id: start, outIdx: 0 }];
    stateMap.set(start, 'inStack');
    while (stack.length > 0) {
      const top = stack[stack.length - 1];
      const outs = outByFrom.get(top.id) ?? [];
      if (top.outIdx >= outs.length) {
        stateMap.set(top.id, 'done');
        stack.pop();
        continue;
      }
      const e = outs[top.outIdx++];
      const tgtState = stateMap.get(e.to);
      if (tgtState === 'inStack' || e.to === e.from) {
        backEdgeIds.add(e.id);
      } else if (tgtState === 'unvisited') {
        stateMap.set(e.to, 'inStack');
        stack.push({ id: e.to, outIdx: 0 });
      }
    }
  };
  const inCount = new Map<string, number>();
  for (const n of line.nodes) inCount.set(n.id, 0);
  for (const e of line.edges) inCount.set(e.to, (inCount.get(e.to) ?? 0) + 1);
  for (const n of line.nodes) {
    if (stateMap.get(n.id) === 'unvisited' && (inCount.get(n.id) ?? 0) === 0) {
      dfs(n.id);
    }
  }
  for (const n of line.nodes) {
    if (stateMap.get(n.id) === 'unvisited') dfs(n.id);
  }

  // Now do a BFS that respects forward edges only, in line.nodes
  // insertion order as a tiebreaker.
  const fwdInCount = new Map<string, number>();
  for (const n of line.nodes) fwdInCount.set(n.id, 0);
  for (const e of line.edges) {
    if (backEdgeIds.has(e.id)) continue;
    fwdInCount.set(e.to, (fwdInCount.get(e.to) ?? 0) + 1);
  }
  const ready: string[] = line.nodes
    .filter((n) => (fwdInCount.get(n.id) ?? 0) === 0)
    .map((n) => n.id);
  const order: string[] = [];
  const fc = new Map(fwdInCount);
  while (ready.length > 0) {
    const id = ready.shift()!;
    order.push(id);
    for (const e of outByFrom.get(id) ?? []) {
      if (backEdgeIds.has(e.id)) continue;
      const left = (fc.get(e.to) ?? 0) - 1;
      fc.set(e.to, left);
      if (left === 0) ready.push(e.to);
    }
  }
  // Anything missed (e.g. node only reachable through back-edges)
  // gets appended in insertion order.
  const seen = new Set(order);
  for (const n of line.nodes) {
    if (!seen.has(n.id)) order.push(n.id);
  }
  return order.map((id) => line.nodes.find((n) => n.id === id)!).filter(Boolean);
}

function entityDisplayName(ref: LineNodeRef, lookups: Lookups): string {
  switch (ref.kind) {
    case 'position':
      return lookups.posById.get(ref.id)?.name ?? ref.id;
    case 'move':
      return lookups.moveById.get(ref.id)?.name ?? ref.id;
    case 'counter':
      return lookups.counterById.get(ref.id)?.name ?? ref.id;
  }
}

function renderStep(
  node: LineNode,
  index: number,
  lookups: Lookups,
  mediaRefs: MediaRefsIndex | null,
): string {
  const kind = node.ref.kind;
  const name = escapeHtml(entityDisplayName(node.ref, lookups));

  let meta = '';
  let description = '';
  let stepsHtml = '';
  let mediaHtml = '';

  if (kind === 'position') {
    const p = lookups.posById.get(node.ref.id);
    if (p) {
      meta = renderMeta([p.zone, p.dominance, p.posture, p.difficulty]);
      description = escapeHtml(p.description ?? '');
      mediaHtml = renderMedia(p.media, mediaRefs?.positions[p.id]);
    }
  } else if (kind === 'move') {
    const m = lookups.moveById.get(node.ref.id);
    if (m) {
      const fromName = lookups.posById.get(m.from)?.name ?? m.from;
      const toName = lookups.posById.get(m.to)?.name ?? m.to;
      meta = renderMeta([
        `${fromName} → ${toName}`,
        m.category,
        m.difficulty,
        m.gi,
      ]);
      description = escapeHtml(m.description ?? '');
      stepsHtml = renderSteps(m.steps);
      mediaHtml = renderMedia(m.media, mediaRefs?.moves[m.id]);
    }
  } else {
    const c = lookups.counterById.get(node.ref.id);
    if (c) {
      const interceptedMove = lookups.moveById.get(c.intercepts);
      const resultPos = lookups.posById.get(c.result);
      meta = renderMeta([
        interceptedMove ? `intercepts: ${interceptedMove.name}` : null,
        resultPos ? `result: ${resultPos.name}` : null,
        c.category,
        c.difficulty,
      ]);
      description = escapeHtml(c.description ?? '');
      stepsHtml = renderSteps(c.steps);
      mediaHtml = renderMedia(c.media, mediaRefs?.counters?.[c.id]);
    }
  }

  const notesHtml = node.notes
    ? `<div class="step-notes">${escapeHtml(node.notes)}</div>`
    : '';

  return `<div class="step">
    <div class="step-head">
      <span class="step-index">${index}.</span>
      <span class="step-tag ${kind}">${kind}</span>
      <span class="step-name">${name}</span>
    </div>
    ${meta ? `<div class="step-meta">${meta}</div>` : ''}
    ${description ? `<div class="step-desc">${description}</div>` : ''}
    ${stepsHtml}
    ${notesHtml}
    ${mediaHtml}
  </div>`;
}

function renderMeta(parts: (string | null | undefined)[]): string {
  const present = parts.filter(
    (p): p is string => typeof p === 'string' && p.length > 0,
  );
  if (present.length === 0) return '';
  return present.map(escapeHtml).join(' · ');
}

function renderSteps(steps: { title: string; body: string }[]): string {
  if (!steps || steps.length === 0) return '';
  const items = steps
    .map(
      (s) =>
        `<li><span class="stitle">${escapeHtml(s.title)}.</span> ${escapeHtml(s.body)}</li>`,
    )
    .join('');
  return `<ol class="step-steps">${items}</ol>`;
}

function renderMedia(
  inline: { url: string; caption?: string }[] | undefined,
  refs:
    | { youtubeId: string; watchUrl: string; note: string }[]
    | undefined,
): string {
  const links: string[] = [];
  if (inline) {
    for (const m of inline) {
      const caption = m.caption ? escapeHtml(m.caption) : 'watch';
      links.push(
        `<a href="${escapeAttr(m.url)}" target="_blank" rel="noopener noreferrer">▶ ${caption}</a>`,
      );
    }
  }
  if (refs) {
    for (const r of refs) {
      const note = r.note ? escapeHtml(truncate(r.note, 60)) : 'youtube ref';
      links.push(
        `<a href="${escapeAttr(r.watchUrl)}" target="_blank" rel="noopener noreferrer">▶ ${note}</a>`,
      );
    }
  }
  if (links.length === 0) return '';
  return `<div class="media">${links.join('')}</div>`;
}

// -----------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : s.slice(0, max - 1) + '…';
}
